import nluApi from '../../api/nlu'
import _ from 'lodash'
import Promise from 'bluebird'
import { VERSIONS_LIMIT_ON_PUBLISH } from '@/config'
import versionsService from '../../services/versions/versions.service'

export default {
  namespaced: true,

  state: {
    isVersionPublishing: false,
    deleteVersionsProcessing: {
      process: false,
      set: []
    },
    recoverVersionProcessing: {
      process: false,
      id: ''
    },
    renameVersionProcessing: {
      process: false,
      id: ''
    },
    publishVersionProcessing: {
      process: false,
      id: ''
    }
  },

  getters: {
    isPublishButtonDisabled (state, getters, rootState, rootGetters) {
      const isStatusPublished = _.get(rootGetters, 'knowledgebase/statuses/currentStatus') === 'PUBLISHED'
      const isTrainingForTestInProgress = rootState.knowledgebase.test.isTrainingForTest
      const isStatusFeatching = _.get(rootGetters, 'knowledgebase/statuses/isStatusFeatching')
      const doesModelHasContexts = _.isEmpty(rootState.knowledgebase.categories.value)
      const isTableLoading = _.get(rootGetters, 'knowledgebase/isTableLoading')

      return state.publishVersionProcessing.process ||
        state.recoverVersionProcessing.process ||
        state.isVersionPublishing ||
        doesModelHasContexts ||
        isTableLoading ||
        (isStatusPublished && !getters.isVersionModified) ||
        isTrainingForTestInProgress || isStatusFeatching
    },

    getAllProductionVersions (state, getters, rootState, rootGetters) {
      return _.get(rootGetters['knowledgebase/getCurrent'], 'versions', [])
    },

    getProductionVersionId (state, getters, rootState, rootGetters) {
      return _.get(rootGetters['knowledgebase/getCurrent'], 'endpoints.PRODUCTION.currentVersionId', null)
    },

    getCreatedDate (state, getters, rootState, rootGetters) {
      return _.get(rootGetters['knowledgebase/getCurrent'], 'createdDate')
    },

    getModifiedDate (state, getters, rootState, rootGetters) {
      return _.get(rootGetters['knowledgebase/getCurrent'], 'modifiedDate')
    },

    getPublishedDateTime (state, getters, rootState, rootGetters) {
      return _.get(rootGetters['knowledgebase/getCurrent'], 'endpoints.PRODUCTION.publishedDateTime')
    },

    getTrainStatusDate (state, getters, rootState, rootGetters) {
      return _.get(rootGetters['knowledgebase/getCurrent'], 'endpoints.PRODUCTION.trainStatus.date')
    },

    getCurrentVersionId (state, getters) {
      return getters.getProductionVersionId === _.toString(getters.getModifiedDate)
        ? getters.getProductionVersionId
        : null // means working copy
    },

    isVersionModified (state, getters) {
      const modifiedDateStamp = _.toNumber(`${getters.getModifiedDate}000`)
      const publishedDateStamp = Date.parse(getters.getPublishedDateTime)
      const isModifiedAfterPublish = modifiedDateStamp > publishedDateStamp
      const isModifiedAfterTrain = modifiedDateStamp > getters.getTrainStatusDate

      return _.isNull(getters.getTrainStatusDate)
        ? isModifiedAfterPublish
        : isModifiedAfterTrain
    },

    versionLabel (state, getters) {
      if (_.isEmpty(getters.getAllProductionVersions)) {
        return (getters.getCreatedDate === getters.getModifiedDate) ? 'New' : 'Edited'
      } else {
        const version = _.find(getters.getAllProductionVersions, ['versionId', getters.getProductionVersionId])
        const name = _.get(version, 'name', 'Unnamed')

        return getters.isVersionModified ? `(M) ${name}` : name
      }
    },

    modelVersions (state, getters, rootState, rootGetters) {
      const current = _.get(rootGetters, 'knowledgebase/getCurrent')

      return _.chain(current)
        .get('versions', [])
        .reject('isRemoved')
        .map((el) => ({
          id: el.versionId,
          name: _.get(el, 'name', 'Unnamed'),
          time: versionsService.formatDate(el.versionId),
          isPublish: el.trainStatus.status === 'PUBLISHED',
          isCurrent: _.toString(el.versionId) === _.toString(getters.getProductionVersionId)
        }))
        .reverse()
        .value()
    }
  },

  actions: {
    async deleteVersions ({ rootState, rootGetters, commit, dispatch }, { set }) {
      try {
        commit('SET_DELETE_VERSIONS_PROCESSING', {
          process: true,
          set
        })
        const removedVersions = await Promise.each(set, (versionId) => nluApi.deleteVersion({
          kbId: rootState.knowledgebase.current,
          versionId
        }))
        const updatedModels = _.map(rootState.knowledgebase.knowledgeBases,
          (model) => (model.id === rootState.knowledgebase.current
            ? {
                ...model,
                versions: _.filter(model.versions,
                  (version) => !_.includes(removedVersions, version.versionId))
              }
            : model))

        commit('knowledgebase/SET_MODELS', updatedModels, { root: true })
      } catch (error) {
        dispatch('knowledgebase/notifications/addError',
          { titleLabel: 'store.versions.deleteVersions.errorNotification.title', error },
          { root: true })
      }
      commit('SET_DELETE_VERSIONS_PROCESSING', {
        process: false,
        set: []
      })
    },

    async recoverVersion ({ rootState, dispatch, commit, rootGetters }, { versionId }) {
      try {
        commit('SET_RECOVER_VERSION_PROCESSING', {
          process: true,
          id: versionId
        })
        await nluApi.recover({
          kbId: rootState.knowledgebase.current,
          versionId
        })
        await dispatch('updateDataAfterTrain')
        commit('SET_RECOVER_VERSION_PROCESSING', {
          process: false,
          id: ''
        })
        dispatch('knowledgebase/notifications/addInfo',
          { titleLabel: 'store.versions.recoverVersion.successNotification.title' },
          { root: true })
      } catch (error) {
        commit('SET_RECOVER_VERSION_PROCESSING', {
          process: false,
          id: ''
        })
        dispatch('knowledgebase/notifications/addError',
          { titleLabel: 'store.versions.recoverVersion.errorNotification.title', error },
          { root: true })
      }
    },

    async renameVersion ({ rootState, rootGetters, commit, dispatch }, { id, name }) {
      try {
        const currentModelVersionId = rootState.knowledgebase.current
        const allModels = rootState.knowledgebase.knowledgeBases
        commit('SET_RENAME_VERSIONS_PROCESSING', { process: true, id })
        const versions = _.cloneDeep(rootGetters['knowledgebase/getCurrent'].versions)
        const index = _.findIndex(versions, ['versionId', id])
        const renamedVersion = _.set(_.find(versions, ['versionId', id]), 'name', name)
        versions.splice(index, 1, renamedVersion)
        await nluApi.updateKnowledgeBase({
          id: currentModelVersionId,
          versions
        })
        const updatedModels = _.map(allModels, model => (
          model.id === currentModelVersionId ? { ...model, versions } : model))

        commit('knowledgebase/SET_MODELS', updatedModels, { root: true })
      } catch (error) {
        dispatch('knowledgebase/notifications/addError',
          { titleLabel: 'store.versions.renameVersion.errorNotification.title', error },
          { root: true })
      } finally {
        commit('SET_RENAME_VERSIONS_PROCESSING', { process: false, id: '' })
      }
    },

    async publishVersion ({ rootState, rootGetters, getters, commit, dispatch }, { versionId = getters.getCurrentVersionId }) {
      try {
        commit('SET_PUBLISH_PROCESS', true)
        commit('SET_PUBLISH_VERSION_PROCESSING', {
          process: true,
          id: versionId
        })
        await nluApi.publish({
          kbId: rootState.knowledgebase.current,
          versionId
        })
        await dispatch('knowledgebase/statuses/fetchStatus', versionId, { root: true })
        await dispatch('knowledgebase/listAllModels', null, { root: true })

        commit('SET_PUBLISH_VERSION_PROCESSING', {
          process: false,
          id: ''
        })
        dispatch('knowledgebase/notifications/addInfo',
          { titleLabel: 'store.versions.publishVersion.successNotification.title' },
          { root: true })
      } catch (error) {
        commit('SET_PUBLISH_VERSION_PROCESSING', { process: false, id: '' })
        dispatch(
          'knowledgebase/notifications/addError',
          { titleLabel: 'store.versions.publishVersion.errorNotification.title', error },
          { root: true }
        )
      }
      commit('SET_PUBLISH_PROCESS', false)
    },

    async trainVersion ({ rootState, getters, rootGetters, commit, dispatch }, { autoPublish = true }) {
      try {
        if (getters.getAllProductionVersions.length >= VERSIONS_LIMIT_ON_PUBLISH) {
          dispatch('knowledgebase/notifications/addError', {
            titleLabel: ['store.versions.trainVersion.errorNotification.maxNumberOfVersions', { count: VERSIONS_LIMIT_ON_PUBLISH }],
            error: 'store.versions.trainVersion.errorNotification.deleteToContinue'
          }, { root: true })

          return
        }

        commit('SET_PUBLISH_PROCESS', true)
        await nluApi.train({
          kbId: rootState.knowledgebase.current,
          autoPublish
        })
        const statusResponse = await dispatch('knowledgebase/statuses/fetchStatus', null, { root: true })

        commit('SET_PUBLISH_PROCESS', false)

        if (statusResponse.status === 'FAILED') {
          dispatch('knowledgebase/notifications/addError', {
            titleLabel: 'store.versions.trainVersion.errorNotification.publishFailed',
            error: statusResponse.message
          }, {
            root: true
          })
        } else {
          dispatch('knowledgebase/notifications/addInfo', {
            titleLabel: 'store.versions.trainVersion.successNotification.title'
          }, {
            root: true
          })

          const currentModelId = rootState.knowledgebase.current
          console.log('currentModelId: ', currentModelId)

          await dispatch('knowledgebase/listAllModels', null, { root: true })
          dispatch('knowledgebase/initNluModel', currentModelId, { root: true })
          await dispatch('knowledgebase/statuses/fetchStatus', null, { root: true })
        }
      } catch (error) {
        dispatch('knowledgebase/notifications/addError', {
          titleLabel: 'store.versions.trainVersion.errorNotification.trainFailed',
          error
        }, { root: true })
      }
      commit('SET_PUBLISH_PROCESS', false)
    },

    updateDataAfterTrain ({ dispatch }) {
      return Promise.all([
        dispatch('knowledgebase/fetchCategories', null, { root: true }),
        dispatch('knowledgebase/listAllModels', null, { root: true }),
        dispatch('knowledgebase/fetchAllApplications', null, { root: true }),
        dispatch('knowledgebase/autogenerated/fetchAutogenerated', { type: 'suggested', firstLoad: true }, { root: true }),
        dispatch('knowledgebase/autogenerated/fetchAutogenerated', { type: 'unrecognized', firstLoad: true }, { root: true }),
        dispatch('knowledgebase/entities/fetchAllPrebuiltEntities', null, { root: true }),
        dispatch('knowledgebase/entities/fetchAllEntities', null, { root: true })
      ])
    }
  },

  mutations: {
    SET_DELETE_VERSIONS_PROCESSING (state, data) {
      _.set(state, 'deleteVersionsProcessing', data)
    },

    SET_RECOVER_VERSION_PROCESSING (state, data) {
      _.set(state, 'recoverVersionProcessing', data)
    },

    SET_RENAME_VERSIONS_PROCESSING (state, data) {
      _.set(state, 'renameVersionProcessing', data)
    },

    SET_PUBLISH_PROCESS (state, value) {
      _.set(state, 'isVersionPublishing', value)
    },

    SET_PUBLISH_VERSION_PROCESSING (state, data) {
      _.set(state, 'publishVersionProcessing', data)
    }
  }
}
